// Map, discovery and fast travel, the data side (decision 0048: "a map screen that fills in as places are found,
// self-marking points of interest, waypoints with fast travel"; the HUD's minimap and the compass strip are the same
// data drawn two ways). Pure and JSON-safe, so a game keeps the state in its sim and its save:
//   - `worldMap(layout)`: what a map draws, read from the layout (regions, paths, water, landmarks, placements);
//   - `Discovery`: a grid of cells over the bounds revealed round the player as they walk, and points of interest that
//     mark themselves when seen (in range, a line of sight the game answers) or reached (in reach); waypoints among
//     them, and the fast-travel check between reached ones. Its state is a compact string of revealed cells and the
//     marks, for the save.
// The game owns every number (cell size, radii, which places are points of interest); the package owns the mechanism.
import type { Layout, PlacementKind, Shape, Vec2 } from "./layout";

/** What a map draws of a layout. */
export interface WorldMap {
  bounds: { x0: number; z0: number; x1: number; z1: number };
  regions: { id: string; shape: Shape; surface: string }[];
  paths: { id: string; points: Vec2[]; width: number; route: boolean }[];
  water: { id: string; kind: "stream" | "pond"; points?: Vec2[]; width?: number; shape?: Shape }[];
  landmarks: { id: string; kind: "rise" | "bowl"; x: number; z: number; radius: number }[];
  placements: { id: string; kind: PlacementKind; piece: string; pos: Vec2; yaw: number }[];
}

/** The map data of a layout (copies: the map never changes the layout). */
export function worldMap(layout: Layout): WorldMap {
  return {
    bounds: { ...layout.bounds },
    regions: layout.regions.map((r) => ({ id: r.id, shape: structuredClone(r.shape) as Shape, surface: r.surface })),
    paths: layout.paths.map((p) => ({ id: p.id, points: p.points.map((q) => [q[0], q[1]] as Vec2), width: p.width, route: !!p.route })),
    water: layout.water.map((w) => ({
      id: w.id,
      kind: w.kind,
      ...(w.points ? { points: w.points.map((q) => [q[0], q[1]] as Vec2) } : {}),
      ...(w.width !== undefined ? { width: w.width } : {}),
      ...(w.shape ? { shape: structuredClone(w.shape) as Shape } : {}),
    })),
    landmarks: layout.landmarks.map((l) => ({ id: l.id, kind: l.kind, x: l.x, z: l.z, radius: l.radius })),
    placements: layout.placements.map((p) => ({ id: p.id, kind: p.kind, piece: p.piece, pos: [p.pos[0], p.pos[1]] as Vec2, yaw: p.yaw ?? 0 })),
  };
}

/** A place the map marks once it is found. */
export interface PointOfInterest {
  id: string;
  /** The game's kind ("waypoint", "town", "camp"…): how the map draws it. */
  kind: string;
  x: number;
  z: number;
  /** Marked when the player is this close and the game says it can be seen (m). 0: only by reaching it. */
  seeRange: number;
  /** Marked as reached this close (m). */
  reachRange: number;
  /** A fast-travel point once reached. */
  waypoint?: boolean;
}

/** How a point of interest was found. Reached outranks seen. */
export type PoiMark = "seen" | "reached";

/** A discovery's state: JSON-safe, for the save. */
export interface DiscoveryState {
  /** The revealed cells: a bitset, row-major, base64. */
  cells: string;
  /** Points of interest found, and how. */
  marks: Record<string, PoiMark>;
  /** The order they were first found (the map's log). */
  order: string[];
}

/** One newly found or upgraded point of interest. */
export interface DiscoveryEvent {
  poi: string;
  mark: PoiMark;
}

/** Why a fast travel was refused (or null when it may go). */
export type TravelRefusal = "unknown" | "not-a-waypoint" | "not-reached" | "same-place";

/** Options for a {@link Discovery}. */
export interface DiscoveryOptions {
  bounds: { x0: number; z0: number; x1: number; z1: number };
  /** A cell's edge (m). */
  cell: number;
  /** Cells whose centre lies within this many metres of the player are revealed (m). */
  revealRadius: number;
  pois: readonly PointOfInterest[];
}

/** Planted defects beside what they break (engine/CLAUDE.md). */
export const DISCOVERY_FAULTS = {
  /** `state()` leaves the revealed cells out (an empty grid is saved): a save and load must lose the revealed map, and
   *  a map spec comparing the discovery across the round trip must go red. */
  cellsNotSaved: false,
};

const b64chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/** Bytes to base64 (no padding needed for the decoder below; kept standard). */
export function bytesToBase64(b: Uint8Array): string {
  let out = "";
  for (let i = 0; i < b.length; i += 3) {
    const n = (b[i]! << 16) | ((b[i + 1] ?? 0) << 8) | (b[i + 2] ?? 0);
    out += b64chars[(n >> 18) & 63]! + b64chars[(n >> 12) & 63]! + (i + 1 < b.length ? b64chars[(n >> 6) & 63]! : "=") + (i + 2 < b.length ? b64chars[n & 63]! : "=");
  }
  return out;
}

/** Base64 to bytes, into an array of `length` (shorter input leaves zeros; longer is cut). */
export function base64ToBytes(s: string, length: number): Uint8Array {
  const out = new Uint8Array(length);
  let o = 0;
  for (let i = 0; i < s.length; i += 4) {
    const v = (k: number): number => {
      const c = s[i + k];
      const j = c === undefined || c === "=" ? 0 : b64chars.indexOf(c);
      return j < 0 ? 0 : j;
    };
    const n = (v(0) << 18) | (v(1) << 12) | (v(2) << 6) | v(3);
    for (const byte of [(n >> 16) & 255, (n >> 8) & 255, n & 255]) if (o < length) out[o++] = byte;
  }
  return out;
}

/** Discovery over a world's bounds: revealed cells and points of interest. Deterministic: feed it the player's
 *  position on sim steps; its state round-trips through JSON. */
export class Discovery {
  readonly cols: number;
  readonly rows: number;
  readonly cell: number;
  readonly bounds: DiscoveryOptions["bounds"];
  readonly pois: readonly PointOfInterest[];
  private readonly radius: number;
  private bits: Uint8Array;
  private revealedCount = 0;
  private marks = new Map<string, PoiMark>();
  private order: string[] = [];
  private byId: Map<string, PointOfInterest>;
  /** Bumped on every change (a view repaints its fog only when this moves). */
  version = 0;
  /** Planted defects (default the module's {@link DISCOVERY_FAULTS}). */
  faults = DISCOVERY_FAULTS;

  constructor(o: DiscoveryOptions) {
    if (!(o.cell > 0)) throw new Error("discovery: a cell needs a size");
    this.cell = o.cell;
    this.bounds = { ...o.bounds };
    this.cols = Math.max(1, Math.ceil((o.bounds.x1 - o.bounds.x0) / o.cell));
    this.rows = Math.max(1, Math.ceil((o.bounds.z1 - o.bounds.z0) / o.cell));
    this.radius = o.revealRadius;
    this.bits = new Uint8Array(Math.ceil((this.cols * this.rows) / 8));
    this.pois = o.pois.map((p) => ({ ...p }));
    this.byId = new Map(this.pois.map((p) => [p.id, p]));
    if (this.byId.size !== this.pois.length) throw new Error("discovery: duplicate point of interest ids");
  }

  /** The cell (col, row) over (x, z), or null outside the bounds. */
  cellAt(x: number, z: number): { col: number; row: number } | null {
    const col = Math.floor((x - this.bounds.x0) / this.cell);
    const row = Math.floor((z - this.bounds.z0) / this.cell);
    if (col < 0 || row < 0 || col >= this.cols || row >= this.rows) return null;
    return { col, row };
  }

  /** Is the cell revealed? */
  revealed(col: number, row: number): boolean {
    if (col < 0 || row < 0 || col >= this.cols || row >= this.rows) return false;
    const i = row * this.cols + col;
    return (this.bits[i >> 3]! & (1 << (i & 7))) !== 0;
  }

  /** Is the ground at (x, z) revealed? */
  revealedAt(x: number, z: number): boolean {
    const c = this.cellAt(x, z);
    return !!c && this.revealed(c.col, c.row);
  }

  /** Reveal the cells round (x, z); returns how many were new. */
  revealAround(x: number, z: number, radius = this.radius): number {
    const r2 = radius * radius;
    const c0 = Math.max(0, Math.floor((x - radius - this.bounds.x0) / this.cell));
    const c1 = Math.min(this.cols - 1, Math.floor((x + radius - this.bounds.x0) / this.cell));
    const r0 = Math.max(0, Math.floor((z - radius - this.bounds.z0) / this.cell));
    const r1 = Math.min(this.rows - 1, Math.floor((z + radius - this.bounds.z0) / this.cell));
    let n = 0;
    for (let row = r0; row <= r1; row++)
      for (let col = c0; col <= c1; col++) {
        const cx = this.bounds.x0 + (col + 0.5) * this.cell - x;
        const cz = this.bounds.z0 + (row + 0.5) * this.cell - z;
        if (cx * cx + cz * cz > r2) continue;
        const i = row * this.cols + col;
        const m = 1 << (i & 7);
        if (this.bits[i >> 3]! & m) continue;
        this.bits[i >> 3]! |= m;
        n++;
      }
    if (n) {
      this.revealedCount += n;
      this.version++;
    }
    return n;
  }

  /** The share of cells revealed, 0..1. */
  fraction(): number {
    return this.revealedCount / (this.cols * this.rows);
  }

  /** Revealed cell count. */
  count(): number {
    return this.revealedCount;
  }

  /** Mark points of interest from where the player stands: reached within `reachRange`, seen within `seeRange` when
   *  `sees(poi)` says so (a line of sight; asked only of points in range and not yet marked as well). Returns the new
   *  marks (a seen point later reached is reported again as reached). */
  check(x: number, z: number, sees: (p: PointOfInterest) => boolean = () => true): DiscoveryEvent[] {
    const out: DiscoveryEvent[] = [];
    for (const p of this.pois) {
      const had = this.marks.get(p.id);
      if (had === "reached") continue;
      const d = Math.hypot(p.x - x, p.z - z);
      let mark: PoiMark | null = null;
      if (d <= p.reachRange) mark = "reached";
      else if (!had && p.seeRange > 0 && d <= p.seeRange && sees(p)) mark = "seen";
      if (!mark) continue;
      this.setMark(p.id, mark);
      out.push({ poi: p.id, mark });
    }
    return out;
  }

  /** Mark a point of interest directly (a quest that reveals a place). Returns whether it changed. */
  mark(id: string, mark: PoiMark): boolean {
    if (!this.byId.has(id)) throw new Error(`discovery: no point of interest "${id}"`);
    const had = this.marks.get(id);
    if (had === mark || had === "reached") return false;
    this.setMark(id, mark);
    return true;
  }

  private setMark(id: string, mark: PoiMark): void {
    if (!this.marks.has(id)) this.order.push(id);
    this.marks.set(id, mark);
    this.version++;
  }

  /** How a point of interest was found, or null. */
  markOf(id: string): PoiMark | null {
    return this.marks.get(id) ?? null;
  }

  /** A point of interest by id. */
  poi(id: string): PointOfInterest | undefined {
    return this.byId.get(id);
  }

  /** The waypoints reached (fast-travel destinations). */
  waypoints(): PointOfInterest[] {
    return this.pois.filter((p) => p.waypoint && this.marks.get(p.id) === "reached");
  }

  /** May the player fast-travel to `to` (from `from`, a waypoint id or null)? Null: yes; else why not. The game adds
   *  its own rules (not in a fight, not in a cutscene) before asking. */
  travelRefusal(to: string, from: string | null = null): TravelRefusal | null {
    const p = this.byId.get(to);
    if (!p) return "unknown";
    if (!p.waypoint) return "not-a-waypoint";
    if (this.marks.get(to) !== "reached") return "not-reached";
    if (from === to) return "same-place";
    return null;
  }

  /** The state, JSON-safe. */
  state(): DiscoveryState {
    const cells = this.faults.cellsNotSaved ? new Uint8Array(this.bits.length) : this.bits;
    return { cells: bytesToBase64(cells), marks: Object.fromEntries(this.order.map((id) => [id, this.marks.get(id)!])), order: [...this.order] };
  }

  /** Put a saved state back (unknown points of interest are dropped; a shorter grid fills with unrevealed cells). */
  restore(s: DiscoveryState): void {
    this.bits = base64ToBytes(s.cells ?? "", this.bits.length);
    let n = 0;
    for (let i = 0; i < this.cols * this.rows; i++) if (this.bits[i >> 3]! & (1 << (i & 7))) n++;
    this.revealedCount = n;
    this.marks = new Map();
    this.order = [];
    for (const id of s.order ?? Object.keys(s.marks ?? {})) {
      const m = s.marks?.[id];
      if (!this.byId.has(id) || (m !== "seen" && m !== "reached")) continue;
      this.order.push(id);
      this.marks.set(id, m);
    }
    this.version++;
  }

  /** A fresh, empty state. */
  static empty(): DiscoveryState {
    return { cells: "", marks: {}, order: [] };
  }
}
